import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings, Plus, Edit, Trash2, Eye, EyeOff, Search, 
  User, FileText, 
  Save, X, Check, BarChart3,
  TrendingUp, Users, Clock, ArrowRight, Upload,
  Image as ImageIcon, Globe, Lock, Unlock,
  Star, Heart, MessageCircle, Download,
  Shield, Bell,
  LayoutDashboard, Briefcase, Quote, Mail, Send,
  Phone, Target, DollarSign,
  Activity, UserPlus, Archive, Sparkles
} from 'lucide-react';
import GradientText from '../components/GradientText';
import { PremiumBackground } from '../components/EnhancedBackground';
import { FloatingOrbs, AnimatedGrid, AnimatedTextReveal, PremiumCard } from '../components/PremiumEffects';
// PremiumButton components not used in current implementation
import { authAPI, blogAPI, contactAPI, leadAPI, portfolioAPI, testimonialAPI, teamAPI, campaignAPI, commentAPI, analyticsAPI } from '../services/api';

const BlogControlPanel = () => {
  const [currentView, setCurrentView] = useState('platforms'); // 'platforms' or 'management'
  const [activeTab, setActiveTab] = useState('posts');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [imagePreview, setImagePreview] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if token exists in localStorage on mount
    return !!localStorage.getItem('sd_admin_token');
  });
  const [showLoginModal, setShowLoginModal] = useState(() => {
    // Only show login modal if no token exists
    return !localStorage.getItem('sd_admin_token');
  });

  // Validate token on mount - if invalid, logout
  React.useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('sd_admin_token');
      if (token) {
        try {
          await authAPI.getMe();
          setIsAuthenticated(true);
          setShowLoginModal(false);
        } catch (err) {
          // Only clear token if it's an auth error, don't logout on network error
          const msg = err.message || '';
          if (msg.includes('Invalid') || msg.includes('expired') || msg.includes('denied') || msg.includes('unauthorized') || msg.includes('Authentication')) {
            authAPI.logout();
            setIsAuthenticated(false);
            setShowLoginModal(true);
          } else {
            console.error("Token validation error:", err);
            // Assume still authenticated if just a network timeout
            setIsAuthenticated(true);
            setShowLoginModal(false);
          }
        }
      }
    };
    if (!isAuthenticated && localStorage.getItem('sd_admin_token')) {
      validateToken();
    }
  }, [isAuthenticated]);

  // Blog posts data from store
  const [blogPosts, setBlogPosts] = useState([]);

  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    author: '',
    status: 'draft',
    tags: [],
    category: '',
    image: '',
    excerpt: '',
    featured: false
  });
  const [loginCredentials, setLoginCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [bytezTopic, setBytezTopic] = useState('');
  const [bytezLoading, setBytezLoading] = useState(false);

  // State for data fetched from API (no mock data)
  const [comments, setComments] = useState([]);
  const [users] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [leads, setLeads] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [team, setTeam] = useState([]);
  const [contactForms, setContactForms] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  // Load all admin data from API after authentication
  const loadAdminData = useCallback(async () => {
    try {
      const [leadsData, portfolioData, testimonialsData, teamData, contactsData, campaignsData, commentsData] = await Promise.all([
        leadAPI.getAll().catch(() => []),
        portfolioAPI.getAll().catch(() => []),
        testimonialAPI.getAll().catch(() => []),
        teamAPI.getAll().catch(() => []),
        contactAPI.getAll().catch(() => []),
        campaignAPI.getAll().catch(() => []),
        commentAPI.getAll().catch(() => [])
      ]);
      setLeads(leadsData || []);
      setPortfolio(portfolioData || []);
      setTestimonials(testimonialsData || []);
      setTeam(teamData || []);
      setContactForms(contactsData || []);
      setCampaigns(campaignsData || []);
      setComments(commentsData || []);
    } catch (err) {
      console.error('Failed to load admin data:', err);
    }
  }, []);

  React.useEffect(() => {
    if (isAuthenticated) {
      loadAdminData();
    }
  }, [isAuthenticated, loadAdminData]);

  // Platform Management Data
  const platforms = [
    {
      id: 'leads',
      name: 'Leads Management',
      description: 'Track and manage potential customers',
      icon: Target,
      color: 'from-blue-500 to-cyan-500',
      stats: leads.length,
      trend: leads.length > 0 ? 'Active' : '—',
      route: 'leads'
    },
    {
      id: 'portfolio',
      name: 'Portfolio Management',
      description: 'Showcase and manage your projects',
      icon: Briefcase,
      color: 'from-green-500 to-emerald-500',
      stats: portfolio.length,
      trend: portfolio.length > 0 ? 'Active' : '—',
      route: 'portfolio'
    },
    {
      id: 'testimonials',
      name: 'Testimonials',
      description: 'Manage client feedback and reviews',
      icon: Quote,
      color: 'from-purple-500 to-pink-500',
      stats: testimonials.length,
      trend: testimonials.length > 0 ? 'Active' : '—',
      route: 'testimonials'
    },
    {
      id: 'team',
      name: 'Team Management',
      description: 'Manage your team members and roles',
      icon: Users,
      color: 'from-orange-500 to-red-500',
      stats: team.length,
      trend: team.length > 0 ? 'Active' : '—',
      route: 'team'
    },
    {
      id: 'blog',
      name: 'Blog Management',
      description: 'Create and manage blog content',
      icon: FileText,
      color: 'from-indigo-500 to-purple-500',
      stats: blogPosts.length,
      trend: blogPosts.length > 0 ? 'Active' : '—',
      route: 'posts'
    },
    {
      id: 'contacts',
      name: 'Contact Forms',
      description: 'Manage website inquiries and messages',
      icon: Mail,
      color: 'from-teal-500 to-cyan-500',
      stats: contactForms.length,
      trend: contactForms.length > 0 ? 'Active' : '—',
      route: 'contacts'
    },
    {
      id: 'campaigns',
      name: 'Email Campaigns',
      description: 'Create and manage marketing campaigns',
      icon: Send,
      color: 'from-pink-500 to-rose-500',
      stats: campaigns.length,
      trend: campaigns.length > 0 ? 'Active' : '—',
      route: 'campaigns'
    },
    {
      id: 'analytics',
      name: 'Analytics Dashboard',
      description: 'View website and business analytics',
      icon: BarChart3,
      color: 'from-yellow-500 to-orange-500',
      stats: blogPosts.reduce((sum, p) => sum + (p.views || 0), 0),
      trend: 'Views',
      route: 'analytics'
    },
    {
      id: 'seo',
      name: 'SEO Tools',
      description: 'Optimize your website for search engines',
      icon: Globe,
      color: 'from-emerald-500 to-teal-500',
      stats: '—',
      trend: '—',
      route: 'seo'
    },
    {
      id: 'media',
      name: 'Media Library',
      description: 'Manage your images and media files',
      icon: ImageIcon,
      color: 'from-violet-500 to-purple-500',
      stats: 0,
      trend: '—',
      route: 'media'
    },
    {
      id: 'notifications',
      name: 'Notifications',
      description: 'System alerts and announcements',
      icon: Bell,
      color: 'from-amber-500 to-yellow-500',
      stats: notifications.length,
      trend: notifications.length > 0 ? 'New' : '—',
      route: 'notifications'
    },
    {
      id: 'backup',
      name: 'Backup & Restore',
      description: 'Data backup and system recovery',
      icon: Shield,
      color: 'from-slate-500 to-gray-500',
      stats: 0,
      trend: '—',
      route: 'backup'
    }
  ];

  const handlePlatformSelect = (platformRoute) => {
    setCurrentView('management');
    setActiveTab(platformRoute);
  };

  const handleBackToPlatforms = () => {
    setCurrentView('platforms');
  };

  // Animation variants for platforms dashboard
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  // Load blog posts on component mount
  const loadBlogPosts = useCallback(async () => {
    try {
      const posts = await blogAPI.getAll();
      setBlogPosts(posts || []);
    } catch (err) {
      console.error('Failed to load blogs:', err);
    }
  }, []);

  React.useEffect(() => {
    if (isAuthenticated) {
      loadBlogPosts();
    }
  }, [isAuthenticated, loadBlogPosts]);

  const stats = {
    totalPosts: blogPosts.length,
    publishedPosts: blogPosts.filter(post => post.status === 'published').length,
    draftPosts: blogPosts.filter(post => post.status === 'draft').length,
    featuredPosts: blogPosts.filter(post => post.featured).length,
    totalViews: blogPosts.reduce((sum, post) => sum + post.views, 0),
    totalLikes: blogPosts.reduce((sum, post) => sum + post.likes, 0),
    totalComments: blogPosts.reduce((sum, post) => sum + (post.comments || 0), 0),
    totalShares: blogPosts.reduce((sum, post) => sum + (post.shares || 0), 0)
  };

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || post.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleLogin = async () => {
    try {
      const result = await authAPI.login(loginCredentials.username, loginCredentials.password);
      authAPI.setToken(result.token, result.user);
      setIsAuthenticated(true);
      setShowLoginModal(false);
    } catch (err) {
      alert(err.message || 'Invalid credentials. Please use the correct admin email and password.');
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        setNewPost({...newPost, image: e.target.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddPost = async () => {
    if (newPost.title && newPost.content && newPost.author) {
      try {
        await blogAPI.create(newPost);
        await loadBlogPosts();
        setNewPost({ title: '', content: '', author: '', status: 'draft', tags: [], category: '', image: '', excerpt: '', featured: false });
        setImagePreview(null);
        setShowAddModal(false);
      } catch (err) {
        alert(err.message || 'Failed to create post.');
      }
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setNewPost(post);
    setImagePreview(post.image || null);
    setBytezTopic('');
    setShowEditModal(true);
  };

  const handleBytezGenerate = async () => {
    const topic = bytezTopic.trim() || newPost.title.trim();
    if (!topic) {
      alert('Enter a topic (or a title) first, then run Bytez.');
      return;
    }
    setBytezLoading(true);
    try {
      const draft = await blogAPI.generateAiDraft(topic);
      const user = authAPI.getUser();
      setNewPost((prev) => ({
        ...prev,
        title: draft.title || prev.title,
        excerpt: draft.excerpt != null && draft.excerpt !== '' ? draft.excerpt : prev.excerpt,
        content: draft.content || prev.content,
        tags: Array.isArray(draft.tags) && draft.tags.length ? draft.tags : prev.tags,
        category: draft.category || prev.category,
        image: draft.image || prev.image,
        author: !String(prev.author || '').trim() && user?.name ? user.name : prev.author
      }));
      if (draft.image) setImagePreview(draft.image);
      if (!draft.image && draft.imageError) {
        alert(
          `Text fields are ready. Image was not added: ${draft.imageError}\n\nTip: Bytez free accounts often allow only one inference at a time—the server now waits between text and image, retries, and tries backup image models. Wait a few seconds and click Generate again, or use Choose Image.`
        );
      }
    } catch (err) {
      alert(err.message || 'Bytez generation failed. Add BYTEZ_API_KEY on the server and try again.');
    } finally {
      setBytezLoading(false);
    }
  };

  const handleUpdatePost = async () => {
    if (editingPost && newPost.title && newPost.content && newPost.author) {
      try {
        await blogAPI.update(editingPost._id || editingPost.id, newPost);
        await loadBlogPosts();
        setShowEditModal(false);
        setEditingPost(null);
        setNewPost({ title: '', content: '', author: '', status: 'draft', tags: [], category: '', image: '', excerpt: '', featured: false });
        setImagePreview(null);
      } catch (err) {
        alert(err.message || 'Failed to update post.');
      }
    }
  };

  const handleDeletePost = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await blogAPI.delete(id);
        await loadBlogPosts();
      } catch (err) {
        alert(err.message || 'Failed to delete post.');
      }
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await blogAPI.changeStatus(id, status);
      await loadBlogPosts();
    } catch (err) {
      alert(err.message || 'Failed to change status.');
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'leads', label: 'Leads', icon: Target },
    { id: 'portfolio', label: 'Portfolio', icon: Briefcase },
    { id: 'testimonials', label: 'Testimonials', icon: Quote },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'posts', label: 'Blog Posts', icon: FileText },
    { id: 'comments', label: 'Comments', icon: MessageCircle },
    { id: 'contacts', label: 'Contact Forms', icon: Mail },
    { id: 'campaigns', label: 'Email Campaigns', icon: Send },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'seo', label: 'SEO Tools', icon: Globe },
    { id: 'media', label: 'Media', icon: ImageIcon },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'backup', label: 'Backup', icon: Shield },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  // Login Modal
  if (showLoginModal && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <PremiumBackground />
        <FloatingOrbs />
        <AnimatedGrid />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10 w-full max-w-md mx-4"
        >
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <Shield className="w-8 h-8 text-white" />
            </motion.div>
            <h2 className="text-2xl font-bold text-white mb-2">Admin Login</h2>
            <p className="text-slate-300">Enter your credentials to access the admin panel</p>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <input
                type="email"
                value={loginCredentials.username}
                onChange={(e) => setLoginCredentials({...loginCredentials, username: e.target.value})}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                placeholder="Enter email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={loginCredentials.password}
                  onChange={(e) => setLoginCredentials({...loginCredentials, password: e.target.value})}
                  className="w-full px-4 py-3 pr-12 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            <button
              onClick={handleLogin}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Lock className="w-5 h-5" />
              Login
            </button>
          </div>
          
        </motion.div>
      </div>
    );
  }

  // Platforms Dashboard View
  if (currentView === 'platforms') {
    return (
      <section className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 text-white relative overflow-hidden">
        <PremiumBackground />
        <FloatingOrbs />
        <AnimatedGrid />
        
        <div className="container-custom pt-20 pb-12">
          <AnimatedTextReveal className="text-center mb-12">
            <motion.h1 
              className="text-4xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              Admin <GradientText className="text-4xl md:text-6xl">Platforms</GradientText>
            </motion.h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Choose a management platform to access its features and tools
            </p>
          </AnimatedTextReveal>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {platforms.map((platform, index) => (
              <motion.div
                key={platform.id}
                variants={itemVariants}
                className="group cursor-pointer"
                onClick={() => handlePlatformSelect(platform.route)}
              >
                <PremiumCard className="h-full group-hover:scale-105 transition-all duration-300">
                  <div className={`w-16 h-16 bg-gradient-to-r ${platform.color} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                    <platform.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-center mb-2 text-white group-hover:text-purple-400 transition-colors">
                    {platform.name}
                  </h3>
                  
                  <p className="text-gray-300 text-center text-sm mb-4">
                    {platform.description}
                  </p>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div>
                      <div className="text-2xl font-bold text-white">{platform.stats}</div>
                      <div className="text-xs text-gray-400">Total Items</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-green-400">{platform.trend}</div>
                      <div className="text-xs text-gray-400">Growth</div>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-center">
                    <ArrowRight className="w-5 h-5 text-purple-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    );
  }

  // Management Interface View
  return (
    <section className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 text-white relative overflow-hidden">
      <PremiumBackground />
      <FloatingOrbs />
      <AnimatedGrid />
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            y: [-20, 20, -20],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 left-10 w-4 h-4 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full opacity-30 blur-sm"
        />
        <motion.div
          animate={{
            y: [20, -20, 20],
            rotate: [0, -5, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-40 right-20 w-6 h-6 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-20 blur-sm"
        />
      </div>

      <div className="relative z-10 py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-xl rounded-full border border-purple-400/30 mb-8"
            >
              <Settings className="w-6 h-6 text-purple-300" />
              <span className="font-semibold text-purple-200">Blog Management</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-5xl md:text-6xl font-black mb-8 leading-tight"
            >
              {platforms.find(p => p.route === activeTab)?.name || 'Blog'} <GradientText>Management</GradientText>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="text-xl text-slate-200 max-w-3xl mx-auto mb-8 leading-relaxed"
            >
              {platforms.find(p => p.route === activeTab)?.description || 'Manage your blog content, track analytics, and optimize your content strategy.'}
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="flex justify-center gap-4"
            >
              <button
                onClick={handleBackToPlatforms}
                className="px-6 py-3 bg-gradient-to-r from-purple-500/20 to-purple-600/20 border border-purple-500/30 rounded-lg text-purple-300 hover:from-purple-500/30 hover:to-purple-600/30 transition-all duration-300 flex items-center gap-2"
              >
                <ArrowRight className="w-5 h-5 rotate-180" />
                Back to Platforms
              </button>
              <button
                onClick={() => {
                  authAPI.logout();
                  setIsAuthenticated(false);
                  setShowLoginModal(true);
                }}
                className="px-6 py-3 bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-500/30 rounded-lg text-red-300 hover:from-red-500/30 hover:to-red-600/30 transition-all duration-300 flex items-center gap-2"
              >
                <Unlock className="w-5 h-5" />
                Logout
              </button>
              <button
                onClick={() => window.open('/blog', '_blank')}
                className="px-6 py-3 bg-gradient-to-r from-blue-500/20 to-blue-600/20 border border-blue-500/30 rounded-lg text-blue-300 hover:from-blue-500/30 hover:to-blue-600/30 transition-all duration-300 flex items-center gap-2"
              >
                <Globe className="w-5 h-5" />
                View Blog
              </button>
            </motion.div>
          </motion.div>

          {/* Stats Overview */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-12"
          >
            {[
              { label: 'Total Posts', value: stats.totalPosts, icon: FileText, color: 'blue' },
              { label: 'Published', value: stats.publishedPosts, icon: Check, color: 'emerald' },
              { label: 'Featured', value: stats.featuredPosts, icon: Star, color: 'yellow' },
              { label: 'Total Views', value: stats.totalViews.toLocaleString(), icon: Eye, color: 'purple' },
              { label: 'Engagement', value: `${stats.totalLikes + stats.totalComments + stats.totalShares}`, icon: Heart, color: 'pink' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 + index * 0.1 }}
                className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/30 transition-all duration-300"
              >
                <div className={`w-12 h-12 bg-gradient-to-r ${
                  stat.color === 'blue' ? 'from-blue-500/20 to-blue-600/20' :
                  stat.color === 'emerald' ? 'from-emerald-500/20 to-emerald-600/20' :
                  stat.color === 'yellow' ? 'from-yellow-500/20 to-yellow-600/20' :
                  stat.color === 'purple' ? 'from-purple-500/20 to-purple-600/20' :
                  'from-pink-500/20 to-pink-600/20'
                } rounded-xl flex items-center justify-center mb-4`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mb-8"
          >
            <div className="flex justify-center">
              <div className="inline-flex rounded-lg bg-slate-800/50 p-1 border border-white/10">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-3 rounded-md text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                      activeTab === tab.id
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Content */}
          {activeTab === 'posts' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              {/* Controls */}
              <div className="flex flex-col md:flex-row gap-4 mb-8">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search posts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                    />
                  </div>
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                >
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
                <button
                  onClick={() => {
                    setBytezTopic('');
                    setShowAddModal(true);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add Post
                </button>
              </div>

              {/* Posts Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Image</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Title</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Author</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Status</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Views</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Date</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPosts.map((post) => (
                      <tr key={post._id || post.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-2">
                          <div className="w-16 h-12 rounded-lg overflow-hidden">
                            {post.image ? (
                              <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-slate-700 flex items-center justify-center">
                                <ImageIcon className="w-4 h-4 text-slate-400" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-2">
                          <div className="font-medium text-white">{post.title}</div>
                          <div className="text-sm text-slate-400">{post.category}</div>
                          {post.featured && <div className="inline-flex items-center gap-1 mt-1">
                            <Star className="w-3 h-3 text-yellow-400 fill-current" />
                            <span className="text-xs text-yellow-400">Featured</span>
                          </div>}
                        </td>
                        <td className="py-4 px-2 text-slate-300">{post.author}</td>
                        <td className="py-4 px-2">
                          <select
                            value={post.status}
                            onChange={(e) => handleStatusChange(post._id || post.id, e.target.value)}
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              post.status === 'published' 
                                ? 'bg-emerald-500/20 text-emerald-300' 
                                : 'bg-yellow-500/20 text-yellow-300'
                            } border-0 focus:outline-none`}
                          >
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                          </select>
                        </td>
                        <td className="py-4 px-2 text-slate-300">{post.views.toLocaleString()}</td>
                        <td className="py-4 px-2 text-slate-300">{post.publishDate}</td>
                        <td className="py-4 px-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditPost(post)}
                              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4 text-blue-400" />
                            </button>
                            <button
                              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                              title="View"
                            >
                              <Eye className="w-4 h-4 text-green-400" />
                            </button>
                            <button
                              onClick={() => handleDeletePost(post._id || post.id)}
                              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {activeTab === 'analytics' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-purple-400" />
                Blog Analytics
              </h3>
              
              {/* Enhanced Analytics Dashboard */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-6 border border-blue-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-300 text-sm font-medium">Total Views</p>
                      <p className="text-2xl font-bold text-white">{stats.totalViews.toLocaleString()}</p>
                    </div>
                    <Eye className="w-8 h-8 text-blue-400" />
                  </div>
                  <div className="mt-2 text-xs text-blue-300">+12% from last month</div>
                </div>

                <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-6 border border-green-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-300 text-sm font-medium">Engagement</p>
                      <p className="text-2xl font-bold text-white">{stats.totalLikes + stats.totalComments}</p>
                    </div>
                    <Heart className="w-8 h-8 text-green-400" />
                  </div>
                  <div className="mt-2 text-xs text-green-300">+8% from last month</div>
                </div>

                <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-6 border border-purple-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-300 text-sm font-medium">Avg. Time</p>
                      <p className="text-2xl font-bold text-white">3:24</p>
                    </div>
                    <Clock className="w-8 h-8 text-purple-400" />
                  </div>
                  <div className="mt-2 text-xs text-purple-300">+5% from last month</div>
                </div>

                <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl p-6 border border-orange-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-300 text-sm font-medium">Bounce Rate</p>
                      <p className="text-2xl font-bold text-white">42%</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-orange-400" />
                  </div>
                  <div className="mt-2 text-xs text-orange-300">-3% from last month</div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-800/30 rounded-xl p-6 border border-white/5">
                  <h4 className="text-lg font-semibold mb-4 text-white">Top Performing Posts</h4>
                  <div className="space-y-4">
                    {blogPosts
                      .filter(post => post.status === 'published')
                      .sort((a, b) => b.views - a.views)
                      .slice(0, 5)
                      .map((post, index) => (
                        <div key={post._id || post.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
                              <span className="text-sm font-bold text-purple-300">{index + 1}</span>
                            </div>
                            <div>
                              <div className="font-medium text-white text-sm">{post.title}</div>
                              <div className="text-xs text-slate-400">{post.views.toLocaleString()} views</div>
                            </div>
                          </div>
                          <div className="text-xs text-slate-400">{post.likes} likes</div>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="bg-slate-800/30 rounded-xl p-6 border border-white/5">
                  <h4 className="text-lg font-semibold mb-4 text-white">Content Categories</h4>
                  <div className="space-y-3">
                    {['AI Insights', 'Technical', 'Business', 'Industry News'].map((category, index) => {
                      const count = blogPosts.filter(post => post.category === category).length;
                      const percentage = stats.totalPosts > 0 ? (count / stats.totalPosts) * 100 : 0;
                      return (
                        <div key={category} className="flex items-center justify-between">
                          <span className="text-slate-300 text-sm">{category}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-slate-700 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-slate-400 w-8">{count}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'media' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <ImageIcon className="w-6 h-6 text-purple-400" />
                  Media Library
                </h3>
                <button
                  onClick={() => {/* Handle image upload */}}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center gap-2"
                >
                  <Upload className="w-5 h-5" />
                  Upload Image
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {blogPosts.map((post) => (
                  post.image && (
                    <div key={post._id || post.id} className="group relative">
                      <div className="aspect-square rounded-lg overflow-hidden bg-slate-700">
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-center justify-center">
                        <div className="flex gap-2">
                          <button className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors">
                            <Eye className="w-4 h-4 text-white" />
                          </button>
                          <button className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors">
                            <Download className="w-4 h-4 text-white" />
                          </button>
                          <button className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors">
                            <Trash2 className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-slate-300 truncate">{post.title}</div>
                    </div>
                  )
                ))}
              </div>
            </motion.div>
          )}

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="space-y-8"
            >
              {/* Overview Cards */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-6 border border-blue-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-300 text-sm font-medium">Total Leads</p>
                      <p className="text-3xl font-bold text-white">{leads.length}</p>
                    </div>
                    <Target className="w-8 h-8 text-blue-400" />
                  </div>
                  <div className="mt-2 text-xs text-blue-300">+15% from last month</div>
                </div>

                <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-6 border border-green-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-300 text-sm font-medium">Active Projects</p>
                      <p className="text-3xl font-bold text-white">{portfolio.filter(p => p.status === 'in-progress').length}</p>
                    </div>
                    <Briefcase className="w-8 h-8 text-green-400" />
                  </div>
                  <div className="mt-2 text-xs text-green-300">3 projects in progress</div>
                </div>

                <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-6 border border-purple-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-300 text-sm font-medium">Team Members</p>
                      <p className="text-3xl font-bold text-white">{team.length}</p>
                    </div>
                    <Users className="w-8 h-8 text-purple-400" />
                  </div>
                  <div className="mt-2 text-xs text-purple-300">All active</div>
                </div>

                <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl p-6 border border-orange-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-300 text-sm font-medium">Revenue</p>
                      <p className="text-3xl font-bold text-white">$2.1M</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-orange-400" />
                  </div>
                  <div className="mt-2 text-xs text-orange-300">+22% from last quarter</div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                    <Activity className="w-5 h-5 text-purple-400" />
                    Recent Activity
                  </h3>
                  <div className="space-y-4">
                    {[
                      { action: 'New lead added', user: 'John Smith', time: '2 hours ago', type: 'lead' },
                      { action: 'Project completed', user: 'E-commerce AI Platform', time: '1 day ago', type: 'project' },
                      { action: 'Testimonial received', user: 'Michael Chen', time: '2 days ago', type: 'testimonial' },
                      { action: 'Email campaign sent', user: 'AI Newsletter', time: '3 days ago', type: 'campaign' }
                    ].map((activity, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                        <div className={`w-2 h-2 rounded-full ${
                          activity.type === 'lead' ? 'bg-green-500' :
                          activity.type === 'project' ? 'bg-blue-500' :
                          activity.type === 'testimonial' ? 'bg-purple-500' : 'bg-orange-500'
                        }`}></div>
                        <div className="flex-1">
                          <p className="text-white text-sm">{activity.action}</p>
                          <p className="text-slate-400 text-xs">{activity.user}</p>
                        </div>
                        <span className="text-slate-500 text-xs">{activity.time}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                    Performance Metrics
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Website Traffic</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-slate-700 rounded-full h-2">
                          <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                        </div>
                        <span className="text-green-400 text-sm">+15%</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Lead Conversion</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-slate-700 rounded-full h-2">
                          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full" style={{ width: '72%' }}></div>
                        </div>
                        <span className="text-blue-400 text-sm">+8%</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Client Satisfaction</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-slate-700 rounded-full h-2">
                          <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full" style={{ width: '94%' }}></div>
                        </div>
                        <span className="text-purple-400 text-sm">4.8/5</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Project Success Rate</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-slate-700 rounded-full h-2">
                          <div className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full" style={{ width: '96%' }}></div>
                        </div>
                        <span className="text-orange-400 text-sm">96%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Leads Management Tab */}
          {activeTab === 'leads' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <Target className="w-6 h-6 text-purple-400" />
                  Leads Management
                </h3>
                <div className="flex gap-3">
                  <select className="px-4 py-2 bg-slate-700/50 border border-white/10 rounded-lg text-white text-sm">
                    <option value="all">All Leads</option>
                    <option value="new">New</option>
                    <option value="qualified">Qualified</option>
                    <option value="contacted">Contacted</option>
                  </select>
                  <button className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add Lead
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Lead</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Company</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Source</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Status</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Value</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Date</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr key={lead.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-purple-300" />
                            </div>
                            <div>
                              <div className="font-medium text-white">{lead.name}</div>
                              <div className="text-sm text-slate-400">{lead.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-2 text-slate-300">{lead.company}</td>
                        <td className="py-4 px-2 text-slate-300">{lead.source}</td>
                        <td className="py-4 px-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            lead.status === 'new' 
                              ? 'bg-blue-500/20 text-blue-300' 
                              : lead.status === 'qualified'
                              ? 'bg-green-500/20 text-green-300'
                              : 'bg-yellow-500/20 text-yellow-300'
                          }`}>
                            {lead.status}
                          </span>
                        </td>
                        <td className="py-4 px-2 text-slate-300">{lead.value}</td>
                        <td className="py-4 px-2 text-slate-300">{lead.date}</td>
                        <td className="py-4 px-2">
                          <div className="flex items-center gap-2">
                            <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Contact">
                              <Phone className="w-4 h-4 text-blue-400" />
                            </button>
                            <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Edit">
                              <Edit className="w-4 h-4 text-green-400" />
                            </button>
                            <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Delete">
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Portfolio Management Tab */}
          {activeTab === 'portfolio' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <Briefcase className="w-6 h-6 text-purple-400" />
                  Portfolio Management
                </h3>
                <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add Project
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {portfolio.map((project) => (
                  <div key={project.id} className="bg-slate-700/30 rounded-xl p-6 border border-white/5">
                    <div className="relative h-48 mb-4 rounded-lg overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 right-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          project.status === 'completed' 
                            ? 'bg-green-500/20 text-green-300' 
                            : 'bg-blue-500/20 text-blue-300'
                        }`}>
                          {project.status}
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <h4 className="text-xl font-bold text-white">{project.title}</h4>
                      <p className="text-slate-400">{project.client}</p>
                      <p className="text-slate-300 text-sm">{project.description}</p>
                      
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center justify-between pt-3 border-t border-white/10">
                        <div className="text-sm text-slate-400">
                          <div>Value: {project.value}</div>
                          <div>Duration: {project.duration}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Edit">
                            <Edit className="w-4 h-4 text-blue-400" />
                          </button>
                          <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="View">
                            <Eye className="w-4 h-4 text-green-400" />
                          </button>
                          <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Delete">
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Comments Moderation Tab */}
          {activeTab === 'comments' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <MessageCircle className="w-6 h-6 text-purple-400" />
                  Comments Moderation
                </h3>
                <div className="flex gap-2">
                  <select className="px-4 py-2 bg-slate-700/50 border border-white/10 rounded-lg text-white text-sm">
                    <option value="all">All Comments</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                {comments.map((comment) => (
                  <div key={comment.id} className="bg-slate-700/30 rounded-xl p-6 border border-white/5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-purple-300" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white">{comment.author}</h4>
                          <p className="text-sm text-slate-400">{comment.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          comment.status === 'approved' 
                            ? 'bg-green-500/20 text-green-300' 
                            : comment.status === 'pending'
                            ? 'bg-yellow-500/20 text-yellow-300'
                            : 'bg-red-500/20 text-red-300'
                        }`}>
                          {comment.status}
                        </span>
                        <span className="text-sm text-slate-400">{comment.date}</span>
                      </div>
                    </div>
                    <p className="text-slate-300 mb-4">{comment.content}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <button className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-300 rounded-lg hover:bg-green-500/30 transition-colors">
                          <Check className="w-4 h-4" />
                          Approve
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-300 rounded-lg hover:bg-red-500/30 transition-colors">
                          <X className="w-4 h-4" />
                          Reject
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 bg-slate-500/20 text-slate-300 rounded-lg hover:bg-slate-500/30 transition-colors">
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <Heart className="w-4 h-4" />
                        <span className="text-sm">{comment.likes}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Users Management Tab */}
          {activeTab === 'users' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <Users className="w-6 h-6 text-purple-400" />
                  User Management
                </h3>
                <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add User
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">User</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Role</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Status</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Posts</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Last Login</th>
                      <th className="text-left py-4 px-2 text-slate-300 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-purple-300" />
                            </div>
                            <div>
                              <div className="font-medium text-white">{user.name}</div>
                              <div className="text-sm text-slate-400">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            user.role === 'admin' 
                              ? 'bg-purple-500/20 text-purple-300' 
                              : 'bg-blue-500/20 text-blue-300'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="py-4 px-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            user.status === 'active' 
                              ? 'bg-green-500/20 text-green-300' 
                              : 'bg-red-500/20 text-red-300'
                          }`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="py-4 px-2 text-slate-300">{user.postsCount}</td>
                        <td className="py-4 px-2 text-slate-300">{user.lastLogin}</td>
                        <td className="py-4 px-2">
                          <div className="flex items-center gap-2">
                            <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Edit">
                              <Edit className="w-4 h-4 text-blue-400" />
                            </button>
                            <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Delete">
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Testimonials Management Tab */}
          {activeTab === 'testimonials' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <Quote className="w-6 h-6 text-purple-400" />
                  Testimonials Management
                </h3>
                <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add Testimonial
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {testimonials.map((testimonial) => (
                  <div key={testimonial.id} className="bg-slate-700/30 rounded-xl p-6 border border-white/5">
                    <div className="flex items-start gap-4 mb-4">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-white">{testimonial.name}</h4>
                        <p className="text-slate-400 text-sm">{testimonial.position}</p>
                        <p className="text-slate-300 text-sm">{testimonial.company}</p>
                        <div className="flex items-center gap-1 mt-2">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                          ))}
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        testimonial.status === 'approved' 
                          ? 'bg-green-500/20 text-green-300' 
                          : 'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {testimonial.status}
                      </span>
                    </div>
                    
                    <p className="text-slate-300 mb-4 italic">"{testimonial.content}"</p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <span className="text-slate-400 text-sm">{testimonial.date}</span>
                      <div className="flex items-center gap-2">
                        <button className="px-3 py-1 bg-green-500/20 text-green-300 rounded text-xs hover:bg-green-500/30 transition-colors">
                          Approve
                        </button>
                        <button className="px-3 py-1 bg-red-500/20 text-red-300 rounded text-xs hover:bg-red-500/30 transition-colors">
                          Reject
                        </button>
                        <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Edit">
                          <Edit className="w-4 h-4 text-blue-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Team Management Tab */}
          {activeTab === 'team' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <Users className="w-6 h-6 text-purple-400" />
                  Team Management
                </h3>
                <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Add Member
                </button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {team.map((member) => (
                  <div key={member.id} className="bg-slate-700/30 rounded-xl p-6 border border-white/5">
                    <div className="text-center mb-4">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-20 h-20 rounded-full object-cover mx-auto mb-3"
                      />
                      <h4 className="text-lg font-semibold text-white">{member.name}</h4>
                      <p className="text-slate-400 text-sm">{member.role}</p>
                      <p className="text-slate-500 text-xs">{member.email}</p>
                    </div>
                    
                    <p className="text-slate-300 text-sm mb-4 text-center">{member.bio}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4 justify-center">
                      {member.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        member.status === 'active' 
                          ? 'bg-green-500/20 text-green-300' 
                          : 'bg-red-500/20 text-red-300'
                      }`}>
                        {member.status}
                      </span>
                      <div className="flex items-center gap-2">
                        <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Edit">
                          <Edit className="w-4 h-4 text-blue-400" />
                        </button>
                        <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Delete">
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Contact Forms Management Tab */}
          {activeTab === 'contacts' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <Mail className="w-6 h-6 text-purple-400" />
                  Contact Forms Management
                </h3>
                <select className="px-4 py-2 bg-slate-700/50 border border-white/10 rounded-lg text-white text-sm">
                  <option value="all">All Inquiries</option>
                  <option value="new">New</option>
                  <option value="responded">Responded</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="space-y-4">
                {contactForms.map((form) => (
                  <div key={form.id} className="bg-slate-700/30 rounded-xl p-6 border border-white/5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-purple-300" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white">{form.name}</h4>
                          <p className="text-slate-400 text-sm">{form.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-slate-400 text-sm">{form.source}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          form.status === 'new' 
                            ? 'bg-blue-500/20 text-blue-300' 
                            : 'bg-green-500/20 text-green-300'
                        }`}>
                          {form.status}
                        </span>
                        <span className="text-slate-400 text-sm">{form.date}</span>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <h5 className="text-white font-medium mb-2">{form.subject}</h5>
                      <p className="text-slate-300 text-sm">{form.message}</p>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <div className="flex items-center gap-2">
                        <button className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-300 rounded-lg hover:bg-blue-500/30 transition-colors">
                          <Mail className="w-4 h-4" />
                          Reply
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-300 rounded-lg hover:bg-green-500/30 transition-colors">
                          <Check className="w-4 h-4" />
                          Mark Responded
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Archive">
                          <Archive className="w-4 h-4 text-slate-400" />
                        </button>
                        <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Delete">
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Email Campaigns Tab */}
          {activeTab === 'campaigns' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <Send className="w-6 h-6 text-purple-400" />
                  Email Campaigns
                </h3>
                <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Create Campaign
                </button>
              </div>

              <div className="space-y-6">
                {campaigns.map((campaign) => (
                  <div key={campaign.id} className="bg-slate-700/30 rounded-xl p-6 border border-white/5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-white">{campaign.name}</h4>
                        <p className="text-slate-400 text-sm">{campaign.subject}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        campaign.status === 'sent' 
                          ? 'bg-green-500/20 text-green-300' 
                          : 'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {campaign.status}
                      </span>
                    </div>
                    
                    <div className="grid md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-slate-600/30 rounded-lg p-3">
                        <p className="text-slate-400 text-xs">Recipients</p>
                        <p className="text-white font-semibold">{campaign.recipients.toLocaleString()}</p>
                      </div>
                      <div className="bg-slate-600/30 rounded-lg p-3">
                        <p className="text-slate-400 text-xs">Sent</p>
                        <p className="text-white font-semibold">{campaign.sent.toLocaleString()}</p>
                      </div>
                      <div className="bg-slate-600/30 rounded-lg p-3">
                        <p className="text-slate-400 text-xs">Opened</p>
                        <p className="text-white font-semibold">{campaign.opened.toLocaleString()}</p>
                      </div>
                      <div className="bg-slate-600/30 rounded-lg p-3">
                        <p className="text-slate-400 text-xs">Clicked</p>
                        <p className="text-white font-semibold">{campaign.clicked.toLocaleString()}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <span className="text-slate-400 text-sm">{campaign.date}</span>
                      <div className="flex items-center gap-2">
                        <button className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded text-xs hover:bg-blue-500/30 transition-colors">
                          Edit
                        </button>
                        <button className="px-3 py-1 bg-green-500/20 text-green-300 rounded text-xs hover:bg-green-500/30 transition-colors">
                          Send
                        </button>
                        <button className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded text-xs hover:bg-purple-500/30 transition-colors">
                          Duplicate
                        </button>
                        <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Delete">
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* SEO Tools Tab */}
          {activeTab === 'seo' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3">
                <Globe className="w-6 h-6 text-purple-400" />
                SEO Tools & Optimization
              </h3>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-800/30 rounded-xl p-6 border border-white/5">
                  <h4 className="text-lg font-semibold mb-4 text-white">SEO Score Analysis</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Overall SEO Score</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-slate-700 rounded-full h-2">
                          <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                        </div>
                        <span className="text-green-400 font-semibold">78/100</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Meta Tags</span>
                      <span className="text-green-400">✓ Good</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Keywords Density</span>
                      <span className="text-yellow-400">⚠ Needs Work</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Page Speed</span>
                      <span className="text-green-400">✓ Excellent</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/30 rounded-xl p-6 border border-white/5">
                  <h4 className="text-lg font-semibold mb-4 text-white">Keyword Suggestions</h4>
                  <div className="space-y-3">
                    {['AI automation', 'machine learning', 'digital transformation', 'business intelligence'].map((keyword, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                        <span className="text-slate-300">{keyword}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">Difficulty: Medium</span>
                          <button className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded text-xs hover:bg-purple-500/30 transition-colors">
                            Add
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <Bell className="w-6 h-6 text-purple-400" />
                  Notifications
                </h3>
                <button className="px-4 py-2 bg-slate-700/50 border border-white/10 rounded-lg text-white text-sm hover:bg-slate-700/70 transition-colors">
                  Mark All Read
                </button>
              </div>

              <div className="space-y-4">
                {notifications.map((notification) => (
                  <div key={notification.id} className={`p-4 rounded-xl border ${
                    notification.read 
                      ? 'bg-slate-700/20 border-white/5' 
                      : 'bg-purple-500/10 border-purple-500/20'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-3 h-3 rounded-full mt-2 ${
                          notification.type === 'info' 
                            ? 'bg-blue-500' 
                            : notification.type === 'success'
                            ? 'bg-green-500'
                            : 'bg-yellow-500'
                        }`}></div>
                        <div>
                          <h4 className="font-semibold text-white">{notification.title}</h4>
                          <p className="text-slate-300 text-sm mt-1">{notification.message}</p>
                          <p className="text-slate-400 text-xs mt-2">{notification.date}</p>
                        </div>
                      </div>
                      {!notification.read && (
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Backup & Restore Tab */}
          {activeTab === 'backup' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3">
                <Shield className="w-6 h-6 text-purple-400" />
                Backup & Restore
              </h3>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-800/30 rounded-xl p-6 border border-white/5">
                  <h4 className="text-lg font-semibold mb-4 text-white">Create Backup</h4>
                  <p className="text-slate-300 mb-6">Create a complete backup of your blog data including posts, media, and settings.</p>
                  <div className="space-y-4">
                    <button className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg font-semibold text-white hover:from-green-600 hover:to-emerald-600 transition-all duration-300 flex items-center justify-center gap-2">
                      <Download className="w-5 h-5" />
                      Create Full Backup
                    </button>
                    <button className="w-full px-6 py-3 bg-slate-700/50 border border-white/10 rounded-lg text-white hover:bg-slate-700/70 transition-colors flex items-center justify-center gap-2">
                      <FileText className="w-5 h-5" />
                      Export Posts Only
                    </button>
                  </div>
                </div>

                <div className="bg-slate-800/30 rounded-xl p-6 border border-white/5">
                  <h4 className="text-lg font-semibold mb-4 text-white">Restore Data</h4>
                  <p className="text-slate-300 mb-6">Restore your blog from a previous backup or import data from another source.</p>
                  <div className="space-y-4">
                    <button className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg font-semibold text-white hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 flex items-center justify-center gap-2">
                      <Upload className="w-5 h-5" />
                      Upload Backup File
                    </button>
                    <div className="text-center">
                      <p className="text-slate-400 text-sm mb-2">Last backup: Jan 15, 2024</p>
                      <p className="text-slate-400 text-sm">Next scheduled: Jan 22, 2024</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-slate-800/30 rounded-xl p-6 border border-white/5">
                <h4 className="text-lg font-semibold mb-4 text-white">Backup History</h4>
                <div className="space-y-3">
                  {[
                    { date: '2024-01-15', size: '2.3 MB', type: 'Full Backup' },
                    { date: '2024-01-14', size: '1.8 MB', type: 'Posts Only' },
                    { date: '2024-01-13', size: '2.1 MB', type: 'Full Backup' }
                  ].map((backup, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
                          <Shield className="w-4 h-4 text-purple-300" />
                        </div>
                        <div>
                          <div className="text-white text-sm">{backup.type}</div>
                          <div className="text-slate-400 text-xs">{backup.date}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-slate-400 text-sm">{backup.size}</span>
                        <button className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded text-xs hover:bg-blue-500/30 transition-colors">
                          Restore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'settings' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            >
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3">
                <Settings className="w-6 h-6 text-purple-400" />
                Blog Settings
              </h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Blog Title
                    </label>
                    <input
                      type="text"
                      defaultValue="Sentient Digital Blog"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Blog Description
                    </label>
                    <textarea
                      rows={4}
                      defaultValue="Insights and updates on AI, technology, and business transformation."
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                    />
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Posts per Page
                    </label>
                    <select className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300">
                      <option value="5">5</option>
                      <option value="10" selected>10</option>
                      <option value="15">15</option>
                      <option value="20">20</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Default Author
                    </label>
                    <input
                      type="text"
                      defaultValue="Sentient Digital Team"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/10">
                <button className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center gap-2">
                  <Save className="w-5 h-5" />
                  Save Settings
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">
                {showAddModal ? 'Add New Post' : 'Edit Post'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setEditingPost(null);
                  setBytezTopic('');
                  setNewPost({ title: '', content: '', author: '', status: 'draft', tags: [], category: '', image: '', excerpt: '', featured: false });
                  setImagePreview(null);
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-slate-400" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="p-4 rounded-xl border border-violet-500/30 bg-violet-500/10 space-y-3">
                <label className="block text-sm font-medium text-slate-200">
                  Bytez AI — topic or angle
                </label>
                <textarea
                  rows={2}
                  value={bytezTopic}
                  onChange={(e) => setBytezTopic(e.target.value)}
                  disabled={bytezLoading}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-400/50 focus:ring-2 focus:ring-violet-400/20 transition-all duration-300 disabled:opacity-50"
                  placeholder="e.g. How small businesses can use AI safely in 2026"
                />
                <button
                  type="button"
                  onClick={handleBytezGenerate}
                  disabled={bytezLoading}
                  className="w-full px-4 py-3 rounded-lg font-semibold text-white bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
                >
                  <Sparkles className="w-5 h-5 shrink-0" />
                  {bytezLoading ? 'Generating with Bytez… (image may take extra time)' : 'Generate title, content & image'}
                </button>
                <p className="text-xs text-slate-400">
                  Uses{' '}
                  <a
                    href="https://bytez.com/models"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-300 hover:underline"
                  >
                    Bytez
                  </a>{' '}
                  on the server (set <code className="text-slate-300">BYTEZ_API_KEY</code> in backend <code className="text-slate-300">.env</code>).
                  If the topic box is empty, your title field is used as the prompt. Free-tier accounts may need a short pause between chat and image; the API waits and retries automatically.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                  placeholder="Enter post title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Author *
                </label>
                <input
                  type="text"
                  value={newPost.author}
                  onChange={(e) => setNewPost({...newPost, author: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                  placeholder="Enter author name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Category
                </label>
                <select
                  value={newPost.category}
                  onChange={(e) => setNewPost({...newPost, category: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                >
                  <option value="">Select Category</option>
                  <option value="AI Insights">AI Insights</option>
                  <option value="Technical">Technical</option>
                  <option value="Business">Business</option>
                  <option value="Case Studies">Case Studies</option>
                  <option value="Industry Trends">Industry Trends</option>
                  <option value="How-To Guides">How-To Guides</option>
                  <option value="Expert Insights">Expert Insights</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Excerpt
                </label>
                <textarea
                  rows={3}
                  value={newPost.excerpt}
                  onChange={(e) => setNewPost({...newPost, excerpt: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                  placeholder="Brief description of the post..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Featured Image
                </label>
                <div className="space-y-4">
                  {imagePreview && (
                    <div className="w-full h-48 rounded-lg overflow-hidden">
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-all duration-300 cursor-pointer flex items-center justify-center gap-2"
                    >
                      <Upload className="w-5 h-5" />
                      Choose Image
                    </label>
                    {newPost.image && (
                      <button
                        onClick={() => { setNewPost({...newPost, image: ''}); setImagePreview(null); }}
                        className="px-4 py-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 hover:bg-red-500/30 transition-all duration-300"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Content *
                </label>
                <textarea
                  rows={8}
                  value={newPost.content}
                  onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                  placeholder="Write your post content here..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Status
                  </label>
                  <select
                    value={newPost.status}
                    onChange={(e) => setNewPost({...newPost, status: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
                <div className="flex items-center justify-center">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newPost.featured}
                      onChange={(e) => setNewPost({...newPost, featured: e.target.checked})}
                      className="w-5 h-5 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-yellow-400" />
                      <span className="text-slate-300 font-medium">Featured Post</span>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <button
                onClick={showAddModal ? handleAddPost : handleUpdatePost}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                {showAddModal ? 'Create Post' : 'Update Post'}
              </button>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setEditingPost(null);
                  setBytezTopic('');
                  setNewPost({ title: '', content: '', author: '', status: 'draft', tags: [], category: '', image: '', excerpt: '', featured: false });
                  setImagePreview(null);
                }}
                className="px-6 py-3 border border-white/20 rounded-lg font-semibold text-slate-300 hover:bg-white/10 transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default BlogControlPanel;
